// eslint-disable-next-line import/named

import { ComboboxOptions, FormInputAssistantBase } from './form-input-assistant-base.js';
import {
  ConvertEnumToFormSelectOptionsArray,
  EnumValueToString,
  FormInputSelectValue,
  MapArrayToFormInputSelectValue
} from './form-input-types.js';
// Accepted layering exception (stable ISO-4217 leaf enum); see webmodule-common/other/currency-formatter.ts.
import { CurrencyCode } from '../../../../scripts/api/dealer-api-interface-quote';
import { DataBinding, UIElementResolver } from '../databinding/databinding.js';
import { DataTracker, EventValue, EventValueGetter, EventValueSetter, FieldType } from '../databinding/data-tracker.js';
import { EventVoidAsync } from '../events.js';
import { formInput } from './form-input.js';
import { formRadioGroup } from './form-radio-group.js';
import { formSelect } from './form-select.js';
import { getToolTip } from '../../tooltip.js';
import { html, TemplateResult } from 'lit';
import { InputEventMap, InputType, PickerEventMap, WebmoduleFormInputEvent } from './input-type.js';
import { isEmptyOrSpace } from '../string-helper-functions.js';
import { noParalellExecutionAsync } from '../../common/helpers/callbacks';
import { when } from 'lit/directives/when.js';

export interface InputClasses {
  id: string;
  classes: string;
}

export interface DropDownMenuDetails {
  isSeperator?: boolean;
  label?: string;
  icon?: string;
  onClick?: WebmoduleFormInputEvent;
}

export class DropDownMenuDefinition {
  public items: DropDownMenuDetails[];

  constructor(items: DropDownMenuDetails[]) {
    this.items = items;
  }

  templates(readonly?: boolean) {
    return this.items.map(item => {
      return item.isSeperator && item.label
        ? html` <webmodule-menu-item disabled>
            <hr class="dropdown-divider" />
            <p class="fw-bold mb-0">${item.label}</p>
            <hr class="dropdown-divider" />
          </webmodule-menu-item>`
        : item.isSeperator
          ? html` <webmodule-menu-item>
              <hr class="dropdown-divider" />
            </webmodule-menu-item>`
          : html` <webmodule-menu-item ?disabled=${!item.onClick || readonly} @click=${item.onClick}
              >${item.label}</webmodule-menu-item
            >`;
    });
  }
}

export type EventPickerResult = (() => PickerResult | undefined) | (() => Promise<PickerResult | undefined>);

export interface PickerResult {
  id: string;
  value: string;
}

export class FormInputAssistant extends FormInputAssistantBase {
  protected _dataTracker: DataTracker | (() => DataTracker);

  get dataTracker(): DataTracker {
    return typeof this._dataTracker === 'function' ? this._dataTracker() : this._dataTracker;
  }

  classes: InputClasses[] = [];
  immediateBindingUpdate = false;

  constructor(
    dataTracker: DataTracker | (() => DataTracker),
    forceReadonly: boolean | (() => boolean) = false,
    immediateBindingUpdate = false
  ) {
    super(forceReadonly);

    this._dataTracker = dataTracker;
    this.immediateBindingUpdate = immediateBindingUpdate;
  }

  get makeImmediate(): boolean {
    const x = this.immediateBindingUpdate;
    this.immediateBindingUpdate = true;
    return x;
  }

  get dataBinding(): DataBinding {
    return this.dataTracker.binder;
  }

  id(name: string): string {
    return `${this.dataTracker.binder.internalId}-${name}`;
  }

  public text(
    fieldName: string,
    title?: string | TemplateResult,
    maxLength?: number,
    events?: InputEventMap
  ): TemplateResult {
    return this.forceReadonly
      ? this.formInputReadOnly({ fieldName, type: 'text', title, maxLength, events })
      : this.formInput({ fieldName, type: 'text', title, maxLength, events });
  }

  /**
   * Data-bound combobox - a thin wrapper over the non-bound {@link FormInputAssistantBase.combobox}
   * that reads/writes a data-tracker field, exactly the way {@link text} binds an input.
   *
   * Provided for FUTURE use - there is intentionally NO consumer yet. The only combobox today (the
   * quote item-group selector) is deliberately NOT data-bound, because it drives a deferred grouping
   * action rather than a plain model field. Reach for this overload when the combobox value should
   * simply read/write a model property: register the field first with `dataTracker.addObjectBinding(...)`
   * (like the other fields), then render it with this helper. On change/blur the chosen value -
   * including a value created via `allow-create` - is written back through the standard binder.
   */
  public comboboxField(
    fieldName: string,
    options: {
      values: FormInputSelectValue[];
      label?: string | TemplateResult;
      toolTip?: string | TemplateResult;
      allowCreate?: boolean;
      getCreateLabel?: (value: string) => string;
      emptyText?: string;
      clearable?: boolean;
      placeholder?: string;
      size?: 'small' | 'medium' | 'large';
      readonly?: boolean;
      class?: string;
      slot?: string;
      events?: ComboboxOptions['events'];
    }
  ): TemplateResult {
    const applyChange = () => this.dataTracker.getBinder(fieldName)?.applyChangeToValue();
    return this.combobox({
      id: this.dataBinding.field(fieldName),
      value: this.dataTracker.getObjectDisplayValue(fieldName) ?? '',
      values: options.values,
      label: options.label,
      toolTip: options.toolTip,
      allowCreate: options.allowCreate,
      getCreateLabel: options.getCreateLabel,
      emptyText: options.emptyText,
      clearable: options.clearable,
      placeholder: options.placeholder,
      size: options.size,
      readonly: this.forceReadonly || (options.readonly ?? false),
      class: options.class,
      slot: options.slot,
      events: {
        // mirror the input helpers: commit the editor value to the model on change/blur, then run
        // any caller-supplied handler
        change: e => {
          applyChange();
          options.events?.change?.(e);
        },
        blur: e => {
          applyChange();
          options.events?.blur?.(e);
        },
        create: options.events?.create,
        input: options.events?.input,
        focus: options.events?.focus
      }
    });
  }

  public text1(
    fieldName: string,
    title?: string | TemplateResult,
    options?: {
      maxLength?: number;
      toolTip?: string | TemplateResult;
      min?: number;
      max?: number;
      placeholder?: string;
      events?: InputEventMap;
      autocaptitalize?: boolean;
      uppercase?: boolean;
      slot?: string;
      slotted?: TemplateResult;
      readonly?: boolean;
      class?: string;
    }
  ): TemplateResult {
    return this.forceReadonly || options?.readonly
      ? this.formInputReadOnly({ fieldName, title, type: 'text', ...options })
      : this.formInput({ fieldName, title, type: 'text', ...options });
  }

  public password(
    fieldName: string,
    title?: string,
    options?: {
      maxLength?: number;
      toolTip?: string | TemplateResult;
      min?: number;
      max?: number;
      placeholder?: string;
      events?: InputEventMap;
      autocaptitalize?: boolean;
      uppercase?: boolean;
      slot?: string;
      slotted?: TemplateResult;
    }
  ): TemplateResult {
    return this.forceReadonly
      ? this.formInputReadOnly({ fieldName, title, type: 'password', ...options })
      : this.formInput({ fieldName, title, type: 'password', ...options });
  }

  public textRequired(fieldName: string, title?: string, maxLength?: number, events?: InputEventMap): TemplateResult {
    return this.forceReadonly
      ? this.formInputReadOnly({ fieldName, type: 'text', title, maxLength, events })
      : this.formInputRequired({ fieldName, type: 'text', title, maxLength, events });
  }

  public textHidden(fieldName: string, title?: string): TemplateResult {
    return this.forceReadonly
      ? this.formInputReadOnly({ fieldName, type: 'hidden', title })
      : this.formInput({ fieldName, type: 'hidden', title });
  }

  public textReadonly(
    fieldName: string,
    title?: string | TemplateResult,
    toolTip?: string | TemplateResult
  ): TemplateResult {
    return this.formInputReadOnly({ fieldName, type: 'text', title, toolTip });
  }

  public textReadonly1(
    fieldName: string,
    title?: string | TemplateResult,
    options?: {
      size: 'small' | 'medium' | 'large';
      maxLength?: number;
      toolTip?: string | TemplateResult;
      min?: number;
      max?: number;
      placeholder?: string;
      events?: InputEventMap;
      autocaptitalize?: boolean;
      uppercase?: boolean;
      slot?: string;
      slotted?: TemplateResult;
    }
  ): TemplateResult {
    return this.formInputReadOnly({ fieldName, title, type: 'text', ...options });
  }

  public checkbox(
    fieldName: string,
    title?: string,
    options?: {
      readonly?: boolean;
      class?: string;
      slot?: string;
      slotted?: TemplateResult;
      events?: InputEventMap;
      toolTip?: string | TemplateResult;
    }
  ): TemplateResult {
    return this.forceReadonly || options?.readonly
      ? this.formInputReadOnly({ fieldName, type: 'checkbox', title, ...options })
      : this.formInput({ fieldName, type: 'checkbox', title, ...options });
  }

  public switch(
    fieldName: string,
    title?: string,
    options?: {
      readonly?: boolean;
      class?: string;
      slot?: string;
      slotted?: TemplateResult;
      events?: InputEventMap;
      toolTip?: string | TemplateResult;
    }
  ): TemplateResult {
    return this.forceReadonly || options?.readonly
      ? this.formInputReadOnly({ fieldName, type: 'switch', title, ...options })
      : this.formInput({ fieldName, type: 'switch', title, ...options });
  }

  public switchReadonly(fieldName: string, title?: string): TemplateResult {
    return this.formInputReadOnly({ fieldName, type: 'switch', title });
  }

  public switchRequired(fieldName: string, title?: string): TemplateResult {
    return this.forceReadonly
      ? this.formInputReadOnly({ fieldName, type: 'switch', title })
      : this.formInputRequired({ fieldName, type: 'switch', title });
  }

  public radioGroupArray(
    fieldName: string,
    values: FormInputSelectValue[],
    options?: {
      title?: string;
      events?: InputEventMap;
      class?: string;
      slot?: string;
      required?: boolean;
      slotted?: TemplateResult;
    }
  ): TemplateResult {
    return this.forceReadonly
      ? this.formRadioGroup(fieldName, values, options?.title, { ...options, readonly: true })
      : this.formRadioGroup(fieldName, values, options?.title, { ...options });
  }

  public radioGroupArrayReadonly(
    fieldName: string,
    values: FormInputSelectValue[],
    options?: { title?: string; events?: InputEventMap; class?: string; slot?: string; required?: boolean }
  ): TemplateResult {
    return this.formRadioGroup(fieldName, values, options?.title, { ...options, readonly: true });
  }

  public radioGroup<EnumeratedType>(
    fieldName: string,
    values: EnumeratedType,

    options: {
      title?: string;
      hideZeroValue?: boolean;
      events?: InputEventMap;
      map: EnumValueToString;
    }
  ): TemplateResult {
    let hideValues: number[] | undefined;

    if (options?.hideZeroValue) hideValues = [0];

    return this.forceReadonly
      ? this.formRadioGroup(
          fieldName,
          ConvertEnumToFormSelectOptionsArray(values, hideValues, options.map),
          options.title,
          {
            readonly: true
          }
        )
      : this.formRadioGroup(
          fieldName,
          ConvertEnumToFormSelectOptionsArray(values, hideValues, options.map),
          options.title,
          {
            events: options?.events
          }
        );
  }

  public radioGroupHideValues<EnumeratedType>(
    fieldName: string,
    options: EnumeratedType,
    map: EnumValueToString,
    title?: string,
    hideValues?: number[]
  ): TemplateResult {
    return this.forceReadonly
      ? this.formRadioGroup(fieldName, ConvertEnumToFormSelectOptionsArray(options, hideValues, map), title, {
          readonly: true
        })
      : this.formRadioGroup(fieldName, ConvertEnumToFormSelectOptionsArray(options, hideValues, map), title);
  }

  public noteEx(
    fieldName: string,
    title?: string,
    options?: {
      maxLength?: number;
      toolTip?: string | TemplateResult;
      placeholder?: string;
      class?: string;
    }
  ): TemplateResult {
    return this.forceReadonly
      ? this.formInputReadOnly({ fieldName, type: 'area', title, ...options })
      : this.formInput({ fieldName, type: 'area', title, ...options });
  }

  public note(
    fieldName: string,
    title?: string,
    maxLength?: number,
    toolTip?: string | TemplateResult,
    placeholder?: string,
    numberOfLines?: number
  ): TemplateResult {
    return this.forceReadonly
      ? this.formInputReadOnly({ fieldName, type: 'area', title, maxLength, numberOfLines })
      : this.formInput({ fieldName, type: 'area', title, maxLength, toolTip, placeholder, numberOfLines });
  }

  public noteRequired(fieldName: string, title?: string, maxLength?: number): TemplateResult {
    return this.forceReadonly
      ? this.formInputReadOnly({ fieldName, type: 'area', title, maxLength })
      : this.formInputRequired({ fieldName, type: 'area', title, maxLength });
  }

  public noteReadonly(fieldName: string, title?: string, numberOfLines?: number): TemplateResult {
    return this.formInputReadOnly({ fieldName, type: 'area', title, numberOfLines });
  }

  public date(
    fieldName: string,
    title: string | undefined,
    type: 'date' | 'datetime-local' = 'datetime-local',
    toolTip?: string | TemplateResult,
    min?: number
  ): TemplateResult {
    return this.forceReadonly
      ? this.formInputReadOnly({ fieldName, type, title, toolTip, min })
      : this.formInput({ fieldName, type, title, toolTip, min });
  }

  public dateRequired(
    fieldName: string,
    title: string | undefined,
    type: 'date' | 'datetime-local' = 'datetime-local',
    toolTip?: string | TemplateResult,
    min?: number
  ): TemplateResult {
    return this.forceReadonly
      ? this.formInputReadOnly({ fieldName, type, title, toolTip, min })
      : this.formInputRequired({ fieldName, type, title, toolTip, min });
  }

  public dateReadonly(
    fieldName: string,
    title: string | undefined,
    type: 'date' | 'datetime-local' = 'datetime-local',
    toolTip?: string | TemplateResult,
    min?: number
  ): TemplateResult {
    return this.formInputReadOnly({ fieldName, type, title, toolTip, min });
  }

  public int(
    fieldName: string,
    title?: string,
    options?: {
      min?: number;
      max?: number;
      step?: number;
      events?: InputEventMap;
      toolTip?: string | TemplateResult;
    }
  ): TemplateResult {
    return this.forceReadonly
      ? this.formInputReadOnly({ fieldName, type: 'number', title, ...options })
      : this.formInput({ fieldName, type: 'number', title, ...options });
  }

  public intRequired(
    fieldName: string,
    title?: string,
    options?: {
      min?: number;
      max?: number;
      hidden?: boolean;
      events?: InputEventMap;
      slotted?: TemplateResult;
      toolTip?: string | TemplateResult;
    }
  ): TemplateResult {
    return this.forceReadonly
      ? this.formInputReadOnly({ fieldName, type: 'number', title, ...options })
      : this.formInputRequired({ fieldName, type: 'number', title, ...options });
  }

  public intReadonly(
    fieldName: string,
    title?: string,
    options?: {
      hidden?: boolean;
      events?: InputEventMap;
      slotted?: TemplateResult;
      toolTip?: string | TemplateResult;
      class?: string;
    }
  ): TemplateResult {
    return this.formInputReadOnly({ fieldName, type: 'number', title, ...options });
  }

  public float(
    fieldName: string,
    title?: string,
    options?: {
      min?: number;
      max?: number;
      step?: number;
      decimalPlaces?: number;
      events?: InputEventMap;
      toolTip?: string | TemplateResult;
    }
  ): TemplateResult {
    return this.forceReadonly
      ? this.formInputReadOnly({ fieldName, type: 'number', title, ...options })
      : this.formInput({ fieldName, type: 'number', title, ...options });
  }

  public floatRequired(fieldName: string, title?: string, min?: number, max?: number): TemplateResult {
    return this.forceReadonly
      ? this.formInputReadOnly({ fieldName, type: 'number', title, min, max })
      : this.formInputRequired({ fieldName, type: 'number', title, min, max });
  }

  public floatReadonly(fieldName: string, title?: string): TemplateResult {
    return this.formInputReadOnly({ fieldName, type: 'number', title });
  }

  public money(
    fieldName: string,
    title?: string,
    options?: {
      toolTip?: string | TemplateResult;
      events?: InputEventMap;
      hidden?: boolean;
      readonly?: boolean;
      slotted?: TemplateResult;
      class?: string;
      locale?: string;
      currency?: CurrencyCode;
    }
  ): TemplateResult {
    return this.forceReadonly || options?.readonly
      ? this.formInputReadOnly({
          fieldName,
          type: 'money',
          title,
          ...options
        })
      : this.formInput({ fieldName, type: 'money', title, ...options });
  }

  public moneyRequired(
    fieldName: string,
    title?: string,
    options?: {
      toolTip?: string | TemplateResult;
      events?: InputEventMap;
      hidden?: boolean;
      slotted?: TemplateResult;
      locale?: string;
      currency?: CurrencyCode;
    }
  ): TemplateResult {
    return this.forceReadonly
      ? this.formInputReadOnly({ fieldName, type: 'money', title, ...options })
      : this.formInputRequired({ fieldName, type: 'money', title, ...options });
  }

  public moneyReadonly(
    fieldName: string,
    title?: string,
    options?: {
      class?: string;
      toolTip?: string | TemplateResult;
      events?: InputEventMap;
      hidden?: boolean;
      slotted?: TemplateResult;
      locale?: string;
      currency?: CurrencyCode;
    }
  ): TemplateResult {
    return this.formInputReadOnly({ fieldName, type: 'money', title, ...options });
  }

  public enumPicker<EnumeratedType>(fieldName: string, values: EnumeratedType, title?: string): TemplateResult {
    return this.formSelect(fieldName, ConvertEnumToFormSelectOptionsArray(values, undefined), {
      title: title,
      readonly: this.forceReadonly
    });
  }

  public enumPickerReadonly<EnumeratedType>(
    fieldName: string,
    options: EnumeratedType,
    title?: string
  ): TemplateResult {
    return this.formSelectReadonly(fieldName, ConvertEnumToFormSelectOptionsArray(options, undefined), { title });
  }

  public enumFilter<EnumeratedType>(fieldName: string, options: EnumeratedType, title?: string): TemplateResult {
    return this.forceReadonly
      ? this.formSelectReadonly(fieldName, ConvertEnumToFormSelectOptionsArray(options, [0]), { title })
      : this.formSelect(fieldName, ConvertEnumToFormSelectOptionsArray(options, [0]), { title });
  }

  public enumFilterReadonly<EnumeratedType>(
    fieldName: string,
    options: EnumeratedType,
    title?: string
  ): TemplateResult {
    return this.formSelectReadonly(fieldName, ConvertEnumToFormSelectOptionsArray(options, [0]), { title });
  }

  public arraySelect<ItemType>(
    fieldName: string,
    items: ItemType[],
    convert: (x: ItemType) => FormInputSelectValue,
    options?: {
      title?: string;
      events?: InputEventMap;
      placeholder?: string;
      toolTip?: string | TemplateResult;
      slot?: string;
      slotted?: TemplateResult;
      class?: string;
      readonly?: boolean;
      multiple?: boolean;
    }
  ): TemplateResult {
    return this.formSelect(fieldName, MapArrayToFormInputSelectValue(items, convert), {
      ...options,
      readonly: this.forceReadonly || options?.readonly
    });
  }

  public arraySelectReadonly<ItemType>(
    fieldName: string,
    items: ItemType[],
    convert: (x: ItemType) => FormInputSelectValue,
    title?: string
  ): TemplateResult {
    return this.formSelectReadonly(fieldName, MapArrayToFormInputSelectValue(items, convert), { title });
  }

  public imagePicker(
    fieldName: string,
    title?: string,
    options?: {
      onDeleteEvent?: EventVoidAsync;
    }
  ) {
    const changeEvent = () => {
      if (this.immediateBindingUpdate) {
        this.dataTracker.getBinder(fieldName)?.applyChangeToValue();
      }
    };

    const onDelete = async () => {
      await options?.onDeleteEvent?.();
    };

    return html` <bs-form-image-upload
      data-id=${this.dataBinding.field(fieldName)}
      .value="${this.dataTracker.getObjectValue(fieldName)}"
      data-label=${title}
      @webmodule-change=${changeEvent}
      @webmodule-delete=${onDelete}
      .canDelete=${options?.onDeleteEvent !== undefined}
    ></bs-form-image-upload>`;
  }

  /**
   *
   * @param fieldName fieldname of the object to read and write data to
   * @param display
   * @param clickEvent
   * @param title optional title
   * @param slotted add extra buttons into the slots
   * @returns
   */

  public picker(
    fieldName: string,
    display: string,
    clickEvent: EventPickerResult | DropDownMenuDefinition,
    title?: string,
    slotted?: TemplateResult,
    events?: PickerEventMap,
    toolTip?: string | TemplateResult
  ) {
    return this.forceReadonly
      ? this.formPicker(fieldName, display, clickEvent, title, false, true, slotted, events, toolTip)
      : this.formPicker(fieldName, display, clickEvent, title, false, false, slotted, events, toolTip);
  }

  public pickerRequired(
    fieldName: string,
    display: string,
    clickEvent: EventPickerResult,
    title?: string,
    slotted?: TemplateResult,
    events?: PickerEventMap
  ) {
    return this.forceReadonly
      ? this.formPicker(fieldName, display, clickEvent, title, true, true, slotted, events)
      : this.formPicker(fieldName, display, clickEvent, title, true, false, slotted, events);
  }

  public pickerReadonly(fieldName: string, display: string, title?: string) {
    return this.formPicker(
      fieldName,
      display,
      () => {
        return undefined;
      },
      title,
      false,
      true
    );
  }

  getClasses(fieldName: string) {
    return this.classes
      .filter(x => x.id === '*' || x.id === fieldName)
      .map(x => x.classes)
      .join(' ');
  }

  public updatePicker(fieldName: string, newValue: PickerResult) {
    const displayFieldName = this.dataBinding.field(`${fieldName}-displaycontainer`);
    this.dataBinding.setValue(fieldName, newValue.id);
    const elem = this.dataBinding.parent.querySelector('#' + displayFieldName) as unknown;
    const elemId = this.dataBinding.parent.querySelector('#' + this.dataBinding.field(fieldName)) as unknown;
    if (this.immediateBindingUpdate) this.dataTracker.getBinder(fieldName)?.applyChangeToValue();
    //this is an Input
    if (elemId) (elemId as any).value = newValue.id;
    //this is a WebModuleInput
    if (elem) (elem as any).value = newValue.value;
  }

  private formInputRequired(options: {
    fieldName: string;
    type?: InputType;
    title?: string | TemplateResult;
    maxLength?: number;
    decimalPlaces?: number;
    toolTip?: string | TemplateResult;
    min?: number;
    max?: number;
    step?: number;
    hidden?: boolean;
    events?: InputEventMap;
    slotted?: TemplateResult;
  }): TemplateResult {
    options.type = options.hidden ? 'hidden' : (options.type ?? 'text');
    options.type = options.type ?? 'text';
    return formInput({
      dataBinding: this.dataBinding,
      dataTracker: this.dataTracker,
      readonly: false,
      ...options,
      immediateBindingUpdate: this.immediateBindingUpdate,
      required: true
    });
  }

  private formInputReadOnly(options: {
    size?: 'small' | 'medium' | 'large';
    fieldName: string;
    type?: InputType;
    title?: string | TemplateResult;
    maxLength?: number;
    decimalPlaces?: number;
    toolTip?: string | TemplateResult;
    min?: number;
    max?: number;
    step?: number;
    class?: string;
    numberOfLines?: number;
    hidden?: boolean;
    events?: InputEventMap;
  }): TemplateResult {
    options.type = options.hidden ? 'hidden' : (options.type ?? 'text');

    return formInput({
      ...options,
      readonly: true,
      required: false,
      dataBinding: this.dataBinding,
      dataTracker: this.dataTracker
    });
  }

  private formInput(options: {
    fieldName: string;
    type?: InputType;
    title?: string | TemplateResult;
    maxLength?: number;
    toolTip?: string | TemplateResult;
    min?: number;
    max?: number;
    placeholder?: string;
    events?: InputEventMap;
    autocaptitalize?: boolean;
    class?: string;
    numberOfLines?: number;
    decimalPlaces?: number;
  }): TemplateResult {
    const classes = this.getClasses(options.fieldName);
    options.class = `${classes ?? ''} ${options.class ?? ''}`.trim();
    if (isEmptyOrSpace(options.class)) {
      options.class = undefined;
    }
    //
    return formInput({
      dataBinding: this.dataBinding,
      dataTracker: this.dataTracker,
      readonly: false,
      required: false,
      ...options,
      immediateBindingUpdate: this.immediateBindingUpdate
    });
  }

  private formRadioGroup(
    fieldName: string,
    values: string | FormInputSelectValue[],
    title?: string,
    options?: {
      readonly?: boolean;
      required?: boolean;
      events?: InputEventMap;
      class?: string;
      slot?: string;
      slotted?: TemplateResult;
    }
  ) {
    const classes = this.getClasses(fieldName);
    if (options) {
      options.class = `${classes ?? ''} ${options.class ?? ''}`.trim();
      if (isEmptyOrSpace(options?.class)) {
        options.class = undefined;
      }
    } else options = { class: classes };
    return formRadioGroup(fieldName, title, values, this.dataBinding, this.dataTracker, {
      ...options,
      immediateBindingUpdate: this.immediateBindingUpdate
    });
  }

  private formPicker(
    fieldName: string,
    display: string,
    clickEvent: EventPickerResult | DropDownMenuDefinition,
    title?: string,
    required = false,
    readonly = false,
    slotted?: TemplateResult,
    events?: PickerEventMap,
    toolTip?: string | TemplateResult
  ) {
    const displayFieldName = this.dataBinding.field(`${fieldName}-displaycontainer`);
    const clPick = noParalellExecutionAsync(async (e: Event) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      const newValue = await (clickEvent as EventPickerResult)();
      if (newValue) {
        this.updatePicker(fieldName, newValue);
      }
      events?.change?.(e);
    });

    const classes = `webmodule-control-left-label  ${this.getClasses(fieldName)}`;
    const isDropdown = clickEvent instanceof DropDownMenuDefinition;

    const buttonTemplate = isDropdown
      ? html`
          <webmodule-dropdown hoist slot="suffix">
            <webmodule-icon-button slot="trigger" name="fas-ellipsis" library="fa"></webmodule-icon-button>
            <webmodule-menu> ${clickEvent.templates()}</webmodule-menu>
          </webmodule-dropdown>
        `
      : html` <webmodule-icon-button
          @click=${clPick}
          slot="suffix"
          name="fas-ellipsis"
          library="fa"
          ?disabled=${readonly}
        ></webmodule-icon-button>`;
    const blurEvent = (e: Event) => {
      events?.blur?.(e);
    };
    return html`
      ${this.textHidden(fieldName)}
      <webmodule-input
        id=${displayFieldName}
        class=${classes}
        type="text"
        ?required=${required}
        ?readonly=${true}
        ?filled=${readonly}
        value=${display}
        size="small"
        @webmodule-blur=${blurEvent}
        @webmodule-focus=${events?.focus}
        @mouseenter=${events?.mouseenter}
        @mouseleave=${events?.mouseleave}
        @webmodule-keydown=${events?.keydown}
        @webmodule-keyup=${events?.keyup}
      >
        ${slotted}
        ${when(title, () => html` <span slot="label"> ${title} ${toolTip ? getToolTip(toolTip) : ''} </span> `)}
        ${buttonTemplate}
      </webmodule-input>
    `;
  }

  private formSelect(
    fieldName: string,
    values: string | FormInputSelectValue[],
    options: {
      title?: string;
      readonly?: boolean;
      required?: boolean;
      placeholder?: string;
      events?: InputEventMap;
      slot?: string;
      slotted?: TemplateResult;
      class?: string;
      toolTip?: string | TemplateResult;
      multiple?: boolean;
    }
  ) {
    return formSelect(
      fieldName,
      options?.placeholder ?? '',
      options.title,
      values,
      this.dataBinding,
      this.dataTracker,
      options?.readonly,
      options.required,
      options.events?.change,
      this.immediateBindingUpdate,
      options?.slot,
      options?.slotted,
      options?.class,
      options?.toolTip,
      this.toolTipMaxSize,
      options?.multiple
    );
  }

  private formSelectReadonly(
    fieldName: string,
    values: string | FormInputSelectValue[],
    options: {
      title?: string;

      required?: boolean;
      placeholder?: string;
      events?: InputEventMap;
      slot?: string;
      slotted?: TemplateResult;
      class?: string;
      toolTip?: string | TemplateResult;
      multiple?: boolean;
    }
  ) {
    return formSelect(
      fieldName,
      options?.placeholder ?? '',
      options.title,
      values,
      this.dataBinding,
      this.dataTracker,
      true,
      options.required,
      options.events?.change,
      this.immediateBindingUpdate,
      options?.slot,
      options?.slotted,
      options?.class,
      options?.toolTip,
      this.toolTipMaxSize,
      options?.multiple
    );
  }
}

export class FormInputAssistantWithTracker extends FormInputAssistant {
  private ui: UIElementResolver;
  private internalDataTracker: DataTracker;
  public data: any = {};

  clear() {
    this.data = {};
    this.internalDataTracker.clear();
  }

  constructor(
    ui: UIElementResolver,
    forceReadonly: boolean | (() => boolean) = false,
    initializer?: (fiaw: FormInputAssistantWithTracker) => void
  ) {
    super(() => this.internalDataTracker, forceReadonly, true);
    this.ui = ui;
    this.internalDataTracker = new DataTracker(new DataBinding(this.ui));
    initializer?.(this);
  }

  addDynamic(
    dataField: string,
    fieldType: FieldType,
    getter: EventValueGetter,
    setter?: EventValueSetter,
    options?: {
      editorFieldName?: string;
      nullable?: boolean;

      readonly?: (() => boolean) | undefined;
    }
  ) {
    this.internalDataTracker.addDynamic(dataField, fieldType, getter, setter, options);
  }

  add(fieldName: string, fieldType: FieldType, value: EventValue, nullable = false) {
    this.data[fieldName] = value;
    this.dataTracker.addDynamic(
      fieldName,
      fieldType,
      () => this.data[fieldName],
      value => (this.data[fieldName] = value),
      { nullable: nullable }
    );
  }
}

export class FormInputAssistantWithResolver extends FormInputAssistant {
  private ui: UIElementResolver;
  private internalDataTracker: DataTracker;

  clear() {
    this.internalDataTracker.clear();
  }

  public getter: (fieldname: string) => EventValue;
  public setter: (fieldname: string, value: EventValue, fieldType: FieldType, nullable: boolean) => void;

  constructor(
    ui: UIElementResolver,
    forceReadonly: boolean | (() => boolean) = false,
    initializer?: (fiaw: FormInputAssistantWithResolver) => void
  ) {
    super(() => this.internalDataTracker, forceReadonly, true);
    this.ui = ui;
    this.internalDataTracker = new DataTracker(new DataBinding(this.ui));
    initializer?.(this);
  }

  addDynamic(
    dataField: string,
    fieldType: FieldType,
    getter: EventValueGetter,
    setter?: EventValueSetter,
    options?: {
      editorFieldName?: string;
      nullable?: boolean;

      readonly?: (() => boolean) | undefined;
    }
  ) {
    this.internalDataTracker.addDynamic(dataField, fieldType, getter, setter, options);
  }

  add(fieldName: string, fieldType: FieldType, nullable = false) {
    this.dataTracker.addDynamic(
      fieldName,
      fieldType,
      () => this.getter?.(fieldName),
      value => this.setter?.(fieldName, value, fieldType, nullable),
      {
        nullable: nullable
      }
    );
  }
}
